import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { GearSix, Buildings, WhatsappLogo, Robot, FloppyDisk } from '@phosphor-icons/react';
import toast, { Toaster } from 'react-hot-toast';

export default function IntegrationConfig() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selectedEmpresa, setSelectedEmpresa] = useState(user?.empresa_id || '');
  const [config, setConfig] = useState({
    sap_config: {
      tipo: 'S/4HANA',
      url: '',
      username: '',
      password: '',
      client: '',
      system_number: '',
      url_sroute_to_sap: '',
      url_sap_to_sroute: ''
    },
    whatsapp_config: {
      account_sid: '',
      auth_token: '',
      from_number: '',
      use_emulator: true
    },
    gemini_config: {
      api_key: '',
      model: 'gemini-3.5-flash'
    }
  });
  const [activeTab, setActiveTab] = useState('sap');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.rol === 'super_admin') {
      fetchCompanies();
    } else if (user?.empresa_id) {
      setSelectedEmpresa(user.empresa_id);
    }
  }, [user]);

  useEffect(() => {
    if (selectedEmpresa) {
      fetchConfig();
    }
  }, [selectedEmpresa]);

  const fetchCompanies = async () => {
    try {
      const res = await api.get('/companies');
      setCompanies(res.data);
      if (res.data.length > 0 && !selectedEmpresa) {
        setSelectedEmpresa(res.data[0].id);
      }
    } catch (error) {
      toast.error('Error cargando empresas');
    }
  };

  const fetchConfig = async () => {
    try {
      const res = await api.get(`/integration-config/${selectedEmpresa}`);
      setConfig({
        sap_config: res.data.sap_config || config.sap_config,
        whatsapp_config: res.data.whatsapp_config || config.whatsapp_config,
        gemini_config: res.data.gemini_config || config.gemini_config,
      });
    } catch (error) {
      // No hay config, usar defaults
    }
  };

  const handleSave = async () => {
    if (!selectedEmpresa) {
      toast.error('Selecciona una empresa');
      return;
    }
    setSaving(true);
    try {
      await api.post('/integration-config', {
        empresa_id: selectedEmpresa,
        ...config
      });
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error guardando');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'sap', label: 'SAP', icon: Buildings, testid: 'tab-sap' },
    { id: 'whatsapp', label: 'WhatsApp', icon: WhatsappLogo, testid: 'tab-whatsapp' },
    { id: 'gemini', label: 'Gemini AI', icon: Robot, testid: 'tab-gemini' },
  ];

  return (
    <div data-testid="integration-config-page">
      <Toaster position="top-right" />
      
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-2">
            Panel de Control · Integraciones
          </p>
          <h1 className="text-4xl font-bold text-neutral-900 tracking-tight">
            Configuración
          </h1>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-config-btn"
          className="flex items-center px-4 py-2.5 bg-blue-700 text-white rounded-md hover:bg-blue-800 disabled:opacity-60 text-sm font-medium"
        >
          <FloppyDisk size={18} className="mr-2" weight="bold" />
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </button>
      </div>

      {/* Company Selector */}
      {user?.rol === 'super_admin' && (
        <div className="mb-6 bg-white border border-neutral-200 rounded-md p-4">
          <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">
            Empresa
          </label>
          <select
            value={selectedEmpresa}
            onChange={(e) => setSelectedEmpresa(e.target.value)}
            data-testid="config-company-select"
            className="w-full max-w-md px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm bg-white"
          >
            <option value="">Selecciona una empresa</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
        <div className="flex border-b border-neutral-200">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={tab.testid}
                className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? 'border-blue-700 text-blue-700 bg-blue-50/50'
                    : 'border-transparent text-neutral-600 hover:text-neutral-900'
                }`}
              >
                <Icon size={18} className="mr-2" weight={isActive ? 'fill' : 'regular'} />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="p-8">
          {/* SAP Tab */}
          {activeTab === 'sap' && (
            <div className="space-y-4 max-w-2xl" data-testid="sap-config-form">
              <div>
                <p className="text-xs uppercase tracking-widest text-neutral-500 mb-3">Sistema SAP</p>
                <h3 className="text-xl font-bold text-neutral-900 mb-4">Conexión ERP</h3>
              </div>
              
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Tipo SAP *</label>
                <select
                  value={config.sap_config.tipo}
                  onChange={(e) => setConfig({ ...config, sap_config: { ...config.sap_config, tipo: e.target.value }})}
                  data-testid="sap-tipo-select"
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm bg-white"
                >
                  <option value="S/4HANA">SAP S/4HANA</option>
                  <option value="R/3">SAP R/3</option>
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">URL Servidor SAP</label>
                <input
                  type="text"
                  value={config.sap_config.url}
                  onChange={(e) => setConfig({ ...config, sap_config: { ...config.sap_config, url: e.target.value }})}
                  data-testid="sap-url-input"
                  placeholder="https://sap.empresa.com:8000"
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm font-mono"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Usuario</label>
                  <input
                    type="text"
                    value={config.sap_config.username}
                    onChange={(e) => setConfig({ ...config, sap_config: { ...config.sap_config, username: e.target.value }})}
                    data-testid="sap-username-input"
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Contraseña</label>
                  <input
                    type="password"
                    value={config.sap_config.password}
                    onChange={(e) => setConfig({ ...config, sap_config: { ...config.sap_config, password: e.target.value }})}
                    data-testid="sap-password-input"
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Client</label>
                  <input
                    type="text"
                    value={config.sap_config.client}
                    onChange={(e) => setConfig({ ...config, sap_config: { ...config.sap_config, client: e.target.value }})}
                    data-testid="sap-client-input"
                    placeholder="100"
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">System Number</label>
                  <input
                    type="text"
                    value={config.sap_config.system_number}
                    onChange={(e) => setConfig({ ...config, sap_config: { ...config.sap_config, system_number: e.target.value }})}
                    data-testid="sap-system-number-input"
                    placeholder="00"
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm font-mono"
                  />
                </div>
              </div>

              {/* Nuevas URLs de Sincronización */}
              <div className="mt-6 pt-6 border-t border-neutral-200">
                <p className="text-xs uppercase tracking-widest text-neutral-500 mb-3">Sincronización de Estados</p>
                <h4 className="text-sm font-bold text-neutral-900 mb-4">URLs de Integración</h4>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">
                  URL SRoute → SAP
                  <span className="ml-2 text-neutral-500 normal-case tracking-normal font-normal">(Enviar actualizaciones a SAP)</span>
                </label>
                <input
                  type="text"
                  value={config.sap_config.url_sroute_to_sap || ''}
                  onChange={(e) => setConfig({ ...config, sap_config: { ...config.sap_config, url_sroute_to_sap: e.target.value }})}
                  data-testid="sap-url-sroute-to-sap-input"
                  placeholder="https://sap.empresa.com/api/sroute/update-status"
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm font-mono"
                />
                <p className="mt-1 text-xs text-neutral-500">
                  Endpoint de SAP para recibir actualizaciones de estado desde SRoute
                </p>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">
                  URL SAP → SRoute
                  <span className="ml-2 text-neutral-500 normal-case tracking-normal font-normal">(Recibir actualizaciones de SAP)</span>
                </label>
                <input
                  type="text"
                  value={config.sap_config.url_sap_to_sroute || ''}
                  onChange={(e) => setConfig({ ...config, sap_config: { ...config.sap_config, url_sap_to_sroute: e.target.value }})}
                  data-testid="sap-url-sap-to-sroute-input"
                  placeholder="https://sroute.empresa.com/api/sap/sync-from-sap"
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm font-mono"
                />
                <p className="mt-1 text-xs text-neutral-500">
                  Endpoint de SRoute que SAP llamará para enviar actualizaciones de estado
                </p>
              </div>

              {/* Tabla de Mapeo de Estados */}
              <div className="mt-6 pt-6 border-t border-neutral-200">
                <p className="text-xs uppercase tracking-widest text-neutral-500 mb-3">Mapeo de Estados</p>
                <h4 className="text-sm font-bold text-neutral-900 mb-4">Equivalencia de Códigos</h4>
                <div className="overflow-hidden border border-neutral-200 rounded-md">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-bold uppercase tracking-widest text-neutral-700">Cód. SRoute</th>
                        <th className="px-4 py-2 text-left text-xs font-bold uppercase tracking-widest text-neutral-700">Estado SRoute</th>
                        <th className="px-4 py-2 text-left text-xs font-bold uppercase tracking-widest text-neutral-700">Cód. SAP</th>
                        <th className="px-4 py-2 text-left text-xs font-bold uppercase tracking-widest text-neutral-700">Estado SAP</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                      <tr><td className="px-4 py-2 font-mono text-sm">0</td><td className="px-4 py-2 text-sm">PENDIENTE</td><td className="px-4 py-2 font-mono text-sm">0</td><td className="px-4 py-2 text-sm">Creado</td></tr>
                      <tr><td className="px-4 py-2 font-mono text-sm">1</td><td className="px-4 py-2 text-sm">PLANIFICADO</td><td className="px-4 py-2 font-mono text-sm">1</td><td className="px-4 py-2 text-sm">Planificado</td></tr>
                      <tr><td className="px-4 py-2 font-mono text-sm">2</td><td className="px-4 py-2 text-sm">NOTIFICADO</td><td className="px-4 py-2 font-mono text-sm">2</td><td className="px-4 py-2 text-sm">Registro</td></tr>
                      <tr><td className="px-4 py-2 font-mono text-sm">3</td><td className="px-4 py-2 text-sm">EN ANDÉN</td><td className="px-4 py-2 font-mono text-sm">3</td><td className="px-4 py-2 text-sm">Inicio de Carga</td></tr>
                      <tr><td className="px-4 py-2 font-mono text-sm">4</td><td className="px-4 py-2 text-sm">CARGANDO</td><td className="px-4 py-2 font-mono text-sm">4</td><td className="px-4 py-2 text-sm">Fin de carga</td></tr>
                      <tr><td className="px-4 py-2 font-mono text-sm">6</td><td className="px-4 py-2 text-sm">EN TRÁNSITO</td><td className="px-4 py-2 font-mono text-sm">6</td><td className="px-4 py-2 text-sm">Inicio de transporte</td></tr>
                      <tr><td className="px-4 py-2 font-mono text-sm">7</td><td className="px-4 py-2 text-sm">COMPLETADO</td><td className="px-4 py-2 font-mono text-sm">7</td><td className="px-4 py-2 text-sm">Fin de transporte</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* WhatsApp Tab */}
          {activeTab === 'whatsapp' && (
            <div className="space-y-4 max-w-2xl" data-testid="whatsapp-config-form">
              <div>
                <p className="text-xs uppercase tracking-widest text-neutral-500 mb-3">Notificaciones</p>
                <h3 className="text-xl font-bold text-neutral-900 mb-4">WhatsApp Business API</h3>
              </div>

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-md">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.whatsapp_config.use_emulator}
                    onChange={(e) => setConfig({ ...config, whatsapp_config: { ...config.whatsapp_config, use_emulator: e.target.checked }})}
                    data-testid="whatsapp-emulator-checkbox"
                    className="mt-1 mr-3"
                  />
                  <div>
                    <span className="font-medium text-neutral-900">Usar Emulador de WhatsApp</span>
                    <p className="text-xs text-neutral-600 mt-1">
                      Ideal para desarrollo y pruebas mientras se tramita la aprobación de WhatsApp Business API con Meta.
                    </p>
                  </div>
                </label>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Account SID (Twilio)</label>
                <input
                  type="text"
                  value={config.whatsapp_config.account_sid}
                  onChange={(e) => setConfig({ ...config, whatsapp_config: { ...config.whatsapp_config, account_sid: e.target.value }})}
                  data-testid="whatsapp-sid-input"
                  disabled={config.whatsapp_config.use_emulator}
                  placeholder="ACxxxxxxxxxxxxxxxxxxxxxx"
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm font-mono disabled:bg-neutral-50 disabled:text-neutral-400"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Auth Token</label>
                <input
                  type="password"
                  value={config.whatsapp_config.auth_token}
                  onChange={(e) => setConfig({ ...config, whatsapp_config: { ...config.whatsapp_config, auth_token: e.target.value }})}
                  data-testid="whatsapp-token-input"
                  disabled={config.whatsapp_config.use_emulator}
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm font-mono disabled:bg-neutral-50 disabled:text-neutral-400"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Número de Origen</label>
                <input
                  type="text"
                  value={config.whatsapp_config.from_number}
                  onChange={(e) => setConfig({ ...config, whatsapp_config: { ...config.whatsapp_config, from_number: e.target.value }})}
                  data-testid="whatsapp-from-input"
                  disabled={config.whatsapp_config.use_emulator}
                  placeholder="+56912345678"
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm font-mono disabled:bg-neutral-50 disabled:text-neutral-400"
                />
              </div>
            </div>
          )}

          {/* Gemini Tab */}
          {activeTab === 'gemini' && (
            <div className="space-y-4 max-w-2xl" data-testid="gemini-config-form">
              <div>
                <p className="text-xs uppercase tracking-widest text-neutral-500 mb-3">Inteligencia Artificial</p>
                <h3 className="text-xl font-bold text-neutral-900 mb-4">Gemini OCR</h3>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-neutral-800">
                  <strong>Configuración gestionada:</strong> Se usa la clave universal Emergent para Gemini 3.5 Flash. 
                  Puedes reemplazarla con tu propia API Key si lo prefieres.
                </p>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Modelo</label>
                <select
                  value={config.gemini_config.model}
                  onChange={(e) => setConfig({ ...config, gemini_config: { ...config.gemini_config, model: e.target.value }})}
                  data-testid="gemini-model-select"
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm bg-white"
                >
                  <option value="gemini-3.5-flash">Gemini 3.5 Flash (Recomendado)</option>
                  <option value="gemini-3.1-pro-preview">Gemini 3.1 Pro Preview</option>
                  <option value="gemini-3-flash-preview">Gemini 3 Flash Preview</option>
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">API Key (Opcional)</label>
                <input
                  type="password"
                  value={config.gemini_config.api_key}
                  onChange={(e) => setConfig({ ...config, gemini_config: { ...config.gemini_config, api_key: e.target.value }})}
                  data-testid="gemini-api-key-input"
                  placeholder="Dejar vacío para usar la clave gestionada"
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm font-mono"
                />
                <p className="text-xs text-neutral-500 mt-2">
                  Obtén tu API key en: <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">aistudio.google.com</a>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
